<script setup>
import { ref, computed, inject } from 'vue';
import { Send } from '@lucide/vue';
import { useTranslations } from '../../composables/useTranslations.js';

const props = defineProps({
    disabled: { type: Boolean, default: false },
    maxLength: { type: Number, default: 2000 },
});
const emit = defineEmits(['send']);

const config = inject('aia.config');
const { t } = useTranslations(config?.locale);

const text = ref('');
const sending = ref(false);
const counterText = computed(() => t('composer.charCount', { used: text.value.length, max: props.maxLength }));

const canSend = computed(() => text.value.trim().length > 0 && !props.disabled);

function submit() {
    if (!canSend.value) return;
    const value = text.value.trim();
    text.value = '';
    // Trigger the paper-plane takeoff animation, then reset.
    sending.value = true;
    if (typeof window !== 'undefined') {
        window.setTimeout(() => { sending.value = false; }, 450);
    }
    emit('send', value);
}

function handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        submit();
    }
}
</script>

<template>
    <form
        class="border-t border-[var(--border)] px-4 py-4"
        @submit.prevent="submit"
    >
        <div class="flex items-end gap-2.5">
            <!-- Animated gradient border frame around the input. -->
            <div class="aia-composer-field flex-1">
                <textarea
                    v-model="text"
                    rows="1"
                    :placeholder="t('composer.placeholder')"
                    :maxlength="maxLength"
                    :disabled="disabled"
                    class="aia-composer-input block w-full resize-none rounded-[13px] bg-[var(--input)] px-3.5 py-3 text-[15px] leading-relaxed text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none disabled:opacity-60"
                    style="min-height: 52px; max-height: 160px;"
                    @keydown="handleKeydown"
                    @input="(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                    }"
                />
            </div>
            <button
                type="submit"
                class="aia-send shrink-0 cursor-pointer"
                :class="{ 'is-sending': sending }"
                :disabled="!canSend"
                :aria-label="t('composer.send')"
            >
                <span class="aia-send-shimmer" aria-hidden="true" />
                <Send :size="18" class="aia-send-icon" />
            </button>
        </div>
        <div class="mt-2 flex items-center justify-between text-[11px] text-[var(--muted-foreground)]">
            <span>{{ t('composer.hint') }}</span>
            <span>{{ counterText }}</span>
        </div>
    </form>
</template>

<style scoped>
/* iOS Safari auto-zooms into any focused input whose font-size is under 16px.
 * That zoom widens the layout past the viewport and produces a horizontal
 * scroll when the keyboard opens. Bumping to 16px on mobile suppresses the
 * zoom without disabling pinch-zoom (which `maximum-scale` would, hurting
 * accessibility). Desktop keeps the tighter 15px from the utility class. */
@media (max-width: 640px) {
    .aia-composer-input {
        font-size: 16px;
    }
}

/* Gradient-border frame: the wrapper is the animated gradient, a 2px pad
 * exposes it as a border, and the inner textarea sits on its own bg. The
 * gradient drifts continuously and brightens on focus. */
.aia-composer-field {
    position: relative;
    padding: 2px;
    border-radius: 15px;
    background: var(--aia-gradient-ai, var(--primary));
    background-size: 220% 220%;
    opacity: 0.75;
    transition: opacity 200ms ease;
    animation: aia-composer-flow 7s ease infinite;
}
.aia-composer-field:focus-within {
    opacity: 1;
    box-shadow: 0 0 0 3px var(--aia-glow, transparent);
}
@keyframes aia-composer-flow {
    0%, 100% { background-position:   0% 50%; }
    50%      { background-position: 100% 50%; }
}

/* ---- Send button ---- */
.aia-send {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 52px;
    width: 52px;
    border-radius: 14px;
    overflow: hidden;
    color: #ffffff;
    background: var(--aia-gradient-ai-accent, var(--primary));
    background-size: 180% 180%;
    box-shadow: 0 6px 18px -6px var(--aia-glow, rgba(0, 0, 0, 0.3));
    transition: transform 180ms cubic-bezier(0.2, 0.65, 0.25, 1),
                box-shadow 220ms ease, opacity 180ms ease;
    animation: aia-send-flow 6s ease infinite;
}
.aia-send:hover:not(:disabled) {
    transform: translateY(-2px) scale(1.04);
    box-shadow: 0 10px 26px -6px var(--aia-glow, rgba(0, 0, 0, 0.4));
}
.aia-send:active:not(:disabled) {
    transform: translateY(0) scale(0.96);
}
.aia-send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    animation: none;
    filter: grayscale(0.3);
}

/* Diagonal shine sweep. A narrow highlight band translated across the button;
 * it starts and ends fully off-screen, so the loop reset is invisible. */
.aia-send-shimmer {
    position: absolute;
    top: -25%;
    bottom: -25%;
    left: 0;
    width: 45%;
    background: linear-gradient(
        100deg,
        transparent 0%,
        rgba(255, 255, 255, 0.55) 50%,
        transparent 100%
    );
    transform: translateX(-250%) skewX(-18deg);
    pointer-events: none;
}
.aia-send:not(:disabled) .aia-send-shimmer {
    animation: aia-send-shine 3.6s ease-in-out infinite;
}

.aia-send-icon {
    position: relative;
    transition: transform 180ms ease;
}
.aia-send:hover:not(:disabled) .aia-send-icon {
    transform: translateX(1px) rotate(8deg);
}

/* Paper-plane takeoff on send: fly up-right + fade, then snap back in. */
.aia-send.is-sending .aia-send-icon {
    animation: aia-send-takeoff 450ms cubic-bezier(0.3, 0.7, 0.2, 1);
}

@keyframes aia-send-flow {
    0%, 100% { background-position:   0% 50%; }
    50%      { background-position: 100% 50%; }
}
@keyframes aia-send-shine {
    0%   { transform: translateX(-250%) skewX(-18deg); }
    50%  { transform: translateX(400%) skewX(-18deg); }
    100% { transform: translateX(400%) skewX(-18deg); }
}
@keyframes aia-send-takeoff {
    0%   { transform: translate(0, 0) rotate(0deg); opacity: 1; }
    45%  { transform: translate(16px, -16px) rotate(20deg); opacity: 0; }
    46%  { transform: translate(-12px, 12px) rotate(0deg); opacity: 0; }
    100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
    .aia-composer-field,
    .aia-send,
    .aia-send-shimmer,
    .aia-send-icon { animation: none !important; }
    .aia-send.is-sending .aia-send-icon { animation: none !important; }
}
</style>
